import { useMutation } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AIJobStatus, BrandVoiceAnalysis, ExampleBrand, PlainText, SuccessfulBrandVoiceAnalysis, Website } from 'types'

interface Data {
  brand_voice_analysis: BrandVoiceAnalysis
}

interface Variables {
  sources: Array<Pick<PlainText, 'id' | 'type'> | Pick<Website, 'id' | 'type'> | Pick<ExampleBrand, 'id' | 'type'>>
  signal?: AbortSignal
}

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

const createBrandVoiceAnalysis = async ({ workspaceId, signal, ...variables }: Variables & { workspaceId: number }) => {
  const res = await sendToApi<{ brand_voice_analysis: BrandVoiceAnalysis }>(`/w/${workspaceId}/brand_voice_analyses`, {
    method: 'POST',
    body: variables,
    signal,
  })

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(res.data)
  }

  return res.data
}

export const useCreateBrandVoiceAnalysisSync = () => {
  const workspaceId = useCurrentWorkspaceId()
  return useMutation<SuccessfulBrandVoiceAnalysis, Error, Variables>(async (vars) => {
    const analysis_result = await createBrandVoiceAnalysis({
      workspaceId,
      ...vars,
    })

    let analysis = analysis_result.brand_voice_analysis
    let interval = 500

    do {
      const res = await sendToApi<{ brand_voice_analysis: BrandVoiceAnalysis }>(
        `/w/${workspaceId}/brand_voice_analyses/${analysis.id}`,
        { signal: vars.signal },
      )

      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }

      analysis = res.data.brand_voice_analysis

      if (analysis?.status === AIJobStatus.Failed) {
        throw new Error('Failed to analyze voice')
      }

      await new Promise((resolve) => {
        setTimeout(resolve, interval)
      })
    } while (![AIJobStatus.Successful, AIJobStatus.Failed].includes(analysis?.status))

    return analysis as SuccessfulBrandVoiceAnalysis
  })
}
